import { parseBullet } from './bullet';
import { defaultSelection, resolveOptionRefs } from './options';
import { isMeasurementUnit, parseQty, toSI, unitDim } from './qty';
import type {
  DirectionSegment,
  Ingredient,
  IngredientRef,
  OptionChoice,
  OptionDef,
  Recipe,
  When,
} from './types';
import { MAIN_VESSEL } from './vessels';

function checkMeasurementUnit(slug: string, where: string, unit: string | undefined): void {
  if (unit && !isMeasurementUnit(unit)) {
    throw new Error(
      `recipe "${slug}": unknown unit "${unit}" in ${where}. Use a canonical measurement unit or set customUnit: true on the ingredient.`,
    );
  }
}

function checkVesselKey(slug: string, where: string, key: string, vesselKeys: Set<string>): void {
  if (vesselKeys.has(key)) return;
  const placeholder = key === MAIN_VESSEL ? '{vessel}' : `{vessel:${key}}`;
  throw new Error(
    `recipe "${slug}" ${where}: bullet uses ${placeholder} but recipe.vessels has no "${key}" entry`,
  );
}

function validateSegment(
  slug: string,
  ingByName: Map<string, Ingredient>,
  where: string,
  seg: DirectionSegment,
  vesselKeys: Set<string>,
): void {
  if (typeof seg === 'string') return;
  if ('vessel' in seg) {
    checkVesselKey(slug, where, seg.key, vesselKeys);
    return;
  }
  if ('ref' in seg) {
    const ing = ingByName.get(seg.ref);
    if (!ing) {
      throw new Error(`recipe "${slug}": ingredient ref "${seg.ref}" not found`);
    }
    if (seg.portion && typeof seg.portion !== 'string') {
      const pUnit = seg.portion.unit;
      if (pUnit && pUnit !== ing.unit) {
        checkMeasurementUnit(slug, `${where} (ref "${seg.ref}")`, pUnit);
      }
    }
  } else if ('quantity' in seg) {
    checkMeasurementUnit(slug, where, seg.unit);
  }
}

function validateIngredient(
  slug: string,
  ing: Recipe['ingredients'][number],
  knownSlugs?: Set<string>,
): void {
  if (ing.unit && !ing.customUnit) {
    checkMeasurementUnit(slug, `ingredient "${ing.name}"`, ing.unit);
  }
  if (ing.recipe && knownSlugs && !knownSlugs.has(ing.recipe)) {
    throw new Error(
      `recipe "${slug}": ingredient "${ing.name}" links to unknown recipe "${ing.recipe}"`,
    );
  }
}

function validateChoiceSteps(slug: string, optKey: string, c: OptionChoice): void {
  for (const [stepKey, step] of Object.entries(c.steps ?? {})) {
    if (step.addTime !== undefined && step.addTime < 0) {
      throw new Error(
        `recipe "${slug}" option "${optKey}" choice "${c.key}" step "${stepKey}": addTime must be ≥ 0`,
      );
    }
  }
}

function validateOption(slug: string, opt: OptionDef): Set<string> {
  const choiceKeys = new Set<string>();
  for (const c of opt.choices) {
    if (choiceKeys.has(c.key)) {
      throw new Error(`recipe "${slug}" option "${opt.key}": duplicate choice "${c.key}"`);
    }
    choiceKeys.add(c.key);
    validateChoiceSteps(slug, opt.key, c);
  }
  if (!choiceKeys.has(opt.default)) {
    throw new Error(
      `recipe "${slug}" option "${opt.key}": default "${opt.default}" is not a choice`,
    );
  }
  return choiceKeys;
}

function validateOptions(slug: string, recipe: Recipe): Map<string, Set<string>> {
  const choicesByOption = new Map<string, Set<string>>();
  const seenKeys = new Set<string>();
  for (const opt of recipe.options ?? []) {
    if (seenKeys.has(opt.key)) {
      throw new Error(`recipe "${slug}": duplicate option key "${opt.key}"`);
    }
    seenKeys.add(opt.key);
    choicesByOption.set(opt.key, validateOption(slug, opt));
  }
  return choicesByOption;
}

function validateWhen(
  slug: string,
  where: string,
  when: When,
  choicesByOption: Map<string, Set<string>>,
): void {
  const [optKey, choiceKey] = when;
  const choices = choicesByOption.get(optKey);
  if (!choices) {
    throw new Error(`recipe "${slug}" ${where}: when references unknown option "${optKey}"`);
  }
  if (!choices.has(choiceKey)) {
    throw new Error(
      `recipe "${slug}" ${where}: when references unknown choice "${choiceKey}" of option "${optKey}"`,
    );
  }
}

function validateDirection(
  slug: string,
  recipe: Recipe,
  direction: Recipe['directions'][number],
  ingByName: Map<string, Ingredient>,
  choicesByOption: Map<string, Set<string>>,
): void {
  const where = `direction "${direction.title}"`;
  const vesselKeys = new Set(Object.keys(recipe.vessels ?? {}));
  if (direction.perVessel && !recipe.vessels?.[MAIN_VESSEL]) {
    throw new Error(
      `recipe "${slug}" ${where}: perVessel:true requires a "${MAIN_VESSEL}" entry in recipe.vessels`,
    );
  }
  if (direction.when) validateWhen(slug, where, direction.when, choicesByOption);
  const defaults = defaultSelection(recipe.options);
  for (const bullet of direction.bullets) {
    const rawText = typeof bullet === 'string' ? bullet : bullet.text;
    if (typeof bullet !== 'string') {
      validateWhen(slug, `${where} bullet`, bullet.when, choicesByOption);
    }
    let segs: DirectionSegment[];
    try {
      const resolved = resolveOptionRefs(rawText, recipe.options, defaults);
      segs = parseBullet(resolved);
    } catch (err) {
      throw new Error(`recipe "${slug}" ${where}: ${(err as Error).message}`);
    }
    for (const seg of segs) {
      validateSegment(slug, ingByName, where, seg, vesselKeys);
    }
  }
}

const UNTRACKED = Symbol('untracked');
type Allocation = number | typeof UNTRACKED;

function refFraction(seg: IngredientRef, ing: Ingredient): Allocation {
  if (!seg.portion) return 1;
  if (seg.portion === 'half') return 0.5;
  if (seg.portion === 'third') return 1 / 3;
  const p = seg.portion;
  if (p.unit === ing.unit) {
    const ingQty = parseQty(ing.quantity);
    if (!ingQty) return UNTRACKED;
    return parseQty(p.quantity) / ingQty;
  }
  const pSI = toSI(p.quantity, p.unit);
  const iSI = toSI(ing.quantity, ing.unit);
  if (pSI === null || iSI === null) return UNTRACKED;
  if (p.unit && ing.unit && unitDim(p.unit) !== unitDim(ing.unit)) return UNTRACKED;
  return pSI / iSI;
}

interface RefStats {
  bareCount: number;
  portionsSum: number | typeof UNTRACKED;
  total: number; // total refs (bare + portion)
}

function whenMatches(when: When | undefined, selection: Record<string, string>): boolean {
  if (!when) return true;
  return selection[when[0]] === when[1];
}

function recordRef(seg: IngredientRef, ing: Ingredient, stats: Map<string, RefStats>): void {
  const s = stats.get(seg.ref) ?? { bareCount: 0, portionsSum: 0, total: 0 };
  s.total++;
  if (!seg.portion) {
    s.bareCount++;
  } else if (s.portionsSum !== UNTRACKED) {
    const frac = refFraction(seg, ing);
    s.portionsSum = frac === UNTRACKED ? UNTRACKED : s.portionsSum + frac;
  }
  stats.set(seg.ref, s);
}

function collectBulletRefs(
  recipe: Recipe,
  defaults: Record<string, string>,
  bullet: Recipe['directions'][number]['bullets'][number],
  ingByName: Map<string, Ingredient>,
  stats: Map<string, RefStats>,
): void {
  const rawText = typeof bullet === 'string' ? bullet : bullet.text;
  const bulletWhen = typeof bullet === 'string' ? undefined : bullet.when;
  if (!whenMatches(bulletWhen, defaults)) return;
  let segs: DirectionSegment[];
  try {
    segs = parseBullet(resolveOptionRefs(rawText, recipe.options, defaults));
  } catch {
    return;
  }
  for (const seg of segs) {
    if (typeof seg !== 'object' || !('ref' in seg)) continue;
    const ing = ingByName.get(seg.ref);
    if (ing?.quantity) recordRef(seg, ing, stats);
  }
}

function collectRefStats(
  recipe: Recipe,
  ingByName: Map<string, Ingredient>,
): Map<string, RefStats> {
  const stats = new Map<string, RefStats>();
  const defaults = defaultSelection(recipe.options);
  for (const direction of recipe.directions) {
    if (!whenMatches(direction.when, defaults)) continue;
    for (const bullet of direction.bullets) {
      collectBulletRefs(recipe, defaults, bullet, ingByName, stats);
    }
  }
  return stats;
}

function checkRefStats(slug: string, ing: Ingredient, s: RefStats | undefined): void {
  if (!s || s.total === 0) {
    throw new Error(`recipe "${slug}": ingredient "${ing.name}" listed but never used in bullets`);
  }
  if (s.bareCount > 1) {
    throw new Error(
      `recipe "${slug}": ingredient "${ing.name}" tagged ${s.bareCount} times without portion; tag once on first use then refer to it plainly`,
    );
  }
  if (s.portionsSum === UNTRACKED) return;
  if (s.portionsSum === 0 && s.bareCount === 1) return;
  if (Math.abs(s.portionsSum - 1) > 0.01) {
    throw new Error(
      `recipe "${slug}": ingredient "${ing.name}" portions sum to ${s.portionsSum.toFixed(3)} (expected 1.0)`,
    );
  }
}

/** Every bullet template in the recipe, including branches the defaults don't select. */
function allBulletTexts(recipe: Recipe): string[] {
  const texts = recipe.directions.flatMap((d) =>
    d.bullets.map((b) => (typeof b === 'string' ? b : b.text)),
  );
  for (const opt of recipe.options ?? []) {
    for (const choice of opt.choices) {
      for (const step of Object.values(choice.steps ?? {})) {
        if (step.text) texts.push(step.text);
      }
    }
  }
  return texts;
}

function collectVesselKeys(recipe: Recipe): Set<string> {
  const used = new Set<string>();
  const defaults = defaultSelection(recipe.options);
  for (const text of allBulletTexts(recipe)) {
    let segs: DirectionSegment[];
    try {
      segs = parseBullet(resolveOptionRefs(text, recipe.options, defaults));
    } catch {
      continue;
    }
    for (const seg of segs) {
      if (typeof seg === 'object' && 'vessel' in seg) used.add(seg.key);
    }
  }
  return used;
}

function validateVessels(slug: string, recipe: Recipe): void {
  const used = collectVesselKeys(recipe);
  // perVessel directions consume the main vessel without naming it in a bullet.
  const hasPerVessel = recipe.directions.some((d) => d.perVessel === true);
  for (const key of Object.keys(recipe.vessels ?? {})) {
    if (used.has(key)) continue;
    if (key === MAIN_VESSEL && hasPerVessel) continue;
    throw new Error(`recipe "${slug}": vessel "${key}" declared but never used in bullets`);
  }
}

function validatePortions(slug: string, recipe: Recipe, ingByName: Map<string, Ingredient>): void {
  const stats = collectRefStats(recipe, ingByName);
  for (const ing of recipe.ingredients) {
    if (!ing.quantity) continue;
    checkRefStats(slug, ing, stats.get(ing.name));
  }
}

export function validateRecipe(slug: string, recipe: Recipe, knownSlugs?: Set<string>): void {
  const ingByName = new Map(recipe.ingredients.map((i) => [i.name, i]));

  for (const ing of recipe.ingredients) {
    validateIngredient(slug, ing, knownSlugs);
  }

  const choicesByOption = validateOptions(slug, recipe);

  const seenTitle = new Map<string, Recipe['directions'][number]>();
  for (const direction of recipe.directions) {
    const prior = seenTitle.get(direction.title);
    if (prior && !(prior.when && direction.when)) {
      throw new Error(`recipe "${slug}": duplicate direction title "${direction.title}"`);
    }
    seenTitle.set(direction.title, direction);
    validateDirection(slug, recipe, direction, ingByName, choicesByOption);
  }

  validateVessels(slug, recipe);
  validatePortions(slug, recipe, ingByName);
}
